/**
 * Search Input Component
 * 
 * Enhanced search with suggestions and filters
 * 
 * Version: 6.2.0
 */
import React, { useState, useRef, useEffect, useCallback, ReactNode } from 'react'
import './SearchInput.css'

interface SearchSuggestion {
  id: string
  label: string
  icon?: ReactNode
  category?: string
  onClick?: () => void
}

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  suggestions?: SearchSuggestion[]
  onSearch?: (value: string) => void
  onSuggestionClick?: (suggestion: SearchSuggestion) => void
  showSuggestions?: boolean
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
  className?: string
}

const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  suggestions = [],
  onSearch,
  onSuggestionClick,
  showSuggestions = true,
  disabled = false,
  size = 'medium',
  className = ''
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Filter suggestions based on input
  const filteredSuggestions = suggestions.filter(s => 
    s.label.toLowerCase().includes(value.toLowerCase())
  )

  // Show dropdown when there are suggestions and input is focused
  useEffect(() => {
    if (isFocused && showSuggestions && filteredSuggestions.length > 0 && value.length > 0) {
      setShowDropdown(true)
    } else {
      setShowDropdown(false)
    }
  }, [isFocused, value, showSuggestions, filteredSuggestions.length])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && filteredSuggestions[selectedIndex]) {
          handleSuggestionClick(filteredSuggestions[selectedIndex])
        } else if (value) {
          onSearch?.(value)
        }
        break
      case 'Escape':
        setShowDropdown(false)
        setSelectedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }, [showDropdown, selectedIndex, filteredSuggestions, value, onSearch])

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    onSuggestionClick?.(suggestion)
    onChange(suggestion.label)
    setShowDropdown(false)
    setSelectedIndex(-1)
  }

  const handleClear = () => {
    onChange('')
    inputRef.current?.focus()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (value) {
      onSearch?.(value)
      setShowDropdown(false)
    }
  }

  // Group suggestions by category
  const groupedSuggestions = filteredSuggestions.reduce((acc, suggestion) => {
    const category = suggestion.category || 'Results'
    if (!acc[category]) acc[category] = []
    acc[category].push(suggestion)
    return acc
  }, {} as Record<string, SearchSuggestion[]>)

  return (
    <div 
      ref={containerRef}
      className={`search-input-container ${className}`}
    >
      <form onSubmit={handleSubmit} className={`search-form ${isFocused ? 'focused' : ''} ${disabled ? 'disabled' : ''}`}>
        <div className={`search-icon-wrapper size-${size}`}>
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`search-input size-${size}`}
        />
        
        {value && (
          <button 
            type="button" 
            className="search-clear-btn"
            onClick={handleClear}
          >
            ✕
          </button>
        )}
        
        {onSearch && (
          <button 
            type="submit" 
            className="search-submit-btn"
            disabled={!value}
          >
            Search
          </button>
        )}
      </form>

      {/* Suggestions Dropdown */}
      {showDropdown && (
        <div className="search-suggestions">
          {Object.entries(groupedSuggestions).map(([category, items]) => (
            <div key={category} className="suggestion-group">
              <div className="suggestion-category">{category}</div>
              {items.map((suggestion, index) => {
                const globalIndex = filteredSuggestions.indexOf(suggestion)
                return (
                  <button
                    key={suggestion.id}
                    className={`suggestion-item ${selectedIndex === globalIndex ? 'selected' : ''}`}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setSelectedIndex(globalIndex)}
                  >
                    {suggestion.icon && (
                      <span className="suggestion-icon">{suggestion.icon}</span>
                    )}
                    <span className="suggestion-label">{suggestion.label}</span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default SearchInput